import React, { useState, useMemo } from 'react';
import { Typography } from '@mui/material';

import {
  COLORS,
  PanelContainer,
  SearchWrap,
  SearchTitle,
  SearchInputBox,
  StyledInputBase,
  FieldChip,
  FieldChipWrapper,
  InfoWrapper,
  FieldInfo,
  FieldsList,
  SubItemsContainer,
  ActionTooltip,
  AccordionChip,
  AccordionLeftGroup,
  AccordionBullet,
  AccordionLabel,
  StyledRemoveIcon,
  StyledAddIcon,
} from './style';
import { Chip } from '../chips';

/**
 * Converts the raw factor list into a tree structure.
 *
 * Important:
 * FactorName             -> Display label
 * FactorInternalName     -> Internal identifier
 * ParentFactorName       -> Parent display label
 * ParentFactorInternalName -> Parent internal identifier
 */
export const transformFactorsToTree = (rawFactors = []) => {
  if (!Array.isArray(rawFactors)) return [];

  const rootItems = [];
  const groupsMap = {};

  rawFactors.forEach((item) => {
    if (!item || !item.FactorName) return;

    const factorName = item.FactorName;

    // FactorInternalName is the internal ID.
    // Fallback to FactorName for backward compatibility.
    const factorInternalName =
      item.FactorInternalName || item.FactorName;

    const isSelected = Boolean(
      item.isSelected || item.selected
    );

    /**
     * Root-level factor
     */
    if (!item.ParentFactorName) {
      rootItems.push({
        // Display properties
        FactorName: factorName,

        // Internal properties
        FactorInternalName: factorInternalName,

        ParentFactorName: '',
        ParentFactorInternalName:
          item.ParentFactorInternalName || '',

        FactorType: item.FactorType || '',

        IsViewEnabled:
          item.IsViewEnabled ??
          item.isViewEnabled ??
          true,

        IsFilterEnabled:
          item.IsFilterEnabled ??
          item.isFilterEnabled ??
          true,

        IsSliceEnabled:
          item.IsSliceEnabled ??
          item.isSliceEnabled ??
          true,

        isSelected,

        children: [],
      });

      return;
    }

    /**
     * Child factor
     */
    const parentFactorName = item.ParentFactorName;

    /**
     * Prefer the parent's internal ID when supplied.
     *
     * If the backend only provides ParentFactorName,
     * we temporarily use the parent display name as the
     * group lookup key. The actual child's internal ID is
     * still preserved separately.
     */
    const parentFactorInternalName =
      item.ParentFactorInternalName ||
      item.ParentFactorInternalId ||
      parentFactorName;

    /**
     * Use the parent's internal identifier as the group key.
     */
    const groupKey = parentFactorInternalName;

    if (!groupsMap[groupKey]) {
      groupsMap[groupKey] = {
        // Parent display label
        FactorName: parentFactorName,

        // Parent internal identifier
        FactorInternalName: parentFactorInternalName,

        ParentFactorName: '',
        ParentFactorInternalName: '',

        FactorType: '',

        isGroup: true,

        IsViewEnabled: null,
        IsFilterEnabled: null,
        IsSliceEnabled: null,

        isSelected: false,

        children: [],
      };
    }

    groupsMap[groupKey].children.push({
      // Display label
      FactorName: factorName,

      // Internal identifier
      FactorInternalName: factorInternalName,

      // Parent display label
      ParentFactorName: parentFactorName,

      // Parent internal identifier
      ParentFactorInternalName:
        parentFactorInternalName,

      FactorType: item.FactorType || '',

      IsViewEnabled:
        item.IsViewEnabled ??
        item.isViewEnabled ??
        true,

      IsFilterEnabled:
        item.IsFilterEnabled ??
        item.isFilterEnabled ??
        true,

      IsSliceEnabled:
        item.IsSliceEnabled ??
        item.isSliceEnabled ??
        true,

      isSelected,
    });
  });

  /**
   * Sort using the display label, not the internal ID.
   */
  const sortByFactorName = (a, b) =>
    (a.FactorName || '').localeCompare(
      b.FactorName || ''
    );

  Object.values(groupsMap).forEach((group) => {
    if (Array.isArray(group.children)) {
      group.children.sort(sortByFactorName);
    }
  });

  /**
   * Keep root items and groups together.
   */
  const combinedArray = [
    ...rootItems,
    ...Object.values(groupsMap),
  ];

  return combinedArray.sort(sortByFactorName);
};

export default function LeftFieldsPanel({
  rawFactors = [],
  fields = [],
  views = [],
  onDragStart,
  onDropToSection,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] =
    useState({});

  const isViewEmpty = views.length === 0;

  /**
   * Build the tree only when the source data changes.
   */
  const parsedFields = useMemo(() => {
    const inputData =
      rawFactors && rawFactors.length > 0
        ? rawFactors
        : fields;

    return transformFactorsToTree(inputData);
  }, [rawFactors, fields]);

  /**
   * Group expansion is tracked using FactorInternalName.
   *
   * This is important because FactorName is only a display
   * label and may not be unique.
   */
  const toggleGroup = (groupFactorInternalName) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupFactorInternalName]:
        !prev[groupFactorInternalName],
    }));
  };

  /**
   * Handles View / Filter / Slice actions.
   *
   * The complete factor object is passed through so the
   * parent component can use FactorInternalName internally.
   */
  const handlePillClick = (
    e,
    actionType,
    field
  ) => {
    e.stopPropagation();
    e.preventDefault();

    if (!field || field.isSelected) return;

    if (
      actionType === 'view' &&
      !field.IsViewEnabled
    ) {
      return;
    }

    if (
      actionType === 'filter' &&
      !field.IsFilterEnabled
    ) {
      return;
    }

    if (
      actionType === 'slice' &&
      !field.IsSliceEnabled
    ) {
      return;
    }

    if (
      actionType !== 'view' &&
      isViewEmpty
    ) {
      return;
    }

    if (onDropToSection) {
      onDropToSection(actionType, field);
    }
  };

  /**
   * Renders the View / Filter / Slice action pills.
   */
  const renderActionPills = (field = null) => {
    const isInfoMenu = field === null;

    if (isInfoMenu) {
      return (
        <ActionTooltip className="info-action-tooltip" isInfoMenu={true}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              padding: '4px 2px',
            }}
          >
            {/* View Legend Item */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <Chip
                type="view"
                variant="action"
                interactive={false}
                style={{ marginLeft: '5px' }}
              />
              <Typography
                variant="body2"
                style={{
                  fontSize: '13px',
                  color: '#1e1e1e',
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                }}
              >
                Focus on specific metrics
              </Typography>
            </div>

            <div style={{ height: '1px', backgroundColor: '#eaeaea' }} />

            {/* Filter Legend Item */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <Chip
                type="filter"
                variant="action"
                interactive={false}
                style={{ marginLeft: '5px' }}
              />
              <Typography
                variant="body2"
                style={{
                  fontSize: '13px',
                  color: '#1e1e1e',
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                }}
              >
                Select data by criteria
              </Typography>
            </div>

            <div style={{ height: '1px', backgroundColor: '#eaeaea' }} />

            {/* Slice Legend Item */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <Chip
                type="slice"
                variant="action"
                interactive={false}
                style={{ marginLeft: '5px' }}
              />
              <Typography
                variant="body2"
                style={{
                  fontSize: '13px',
                  color: '#1e1e1e',
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                }}
              >
                Break down data by segment
              </Typography>
            </div>
          </div>
        </ActionTooltip>
      );
    }

    const showView = field.IsViewEnabled !== false;
    const showFilter = field.IsFilterEnabled !== false;
    const showSlice = field.IsSliceEnabled !== false;

    return (
      <ActionTooltip className="hover-action-tooltip" isInfoMenu={false}>
        {showView && (
          <Chip
            type="view"
            variant="action"
            disabled={Boolean(
              field && (!field.IsViewEnabled || field.isSelected)
            )}
            onClick={(e) => handlePillClick(e, 'view', field)}
          />
        )}

        {showFilter && (
          <Chip
            type="filter"
            variant="action"
            disabled={Boolean(
              field &&
                (!field.IsFilterEnabled || isViewEmpty || field.isSelected)
            )}
            onClick={(e) => handlePillClick(e, 'filter', field)}
          />
        )}

        {showSlice && (
          <Chip
            type="slice"
            variant="action"
            disabled={Boolean(
              field &&
                (!field.IsSliceEnabled || isViewEmpty || field.isSelected)
            )}
            onClick={(e) => handlePillClick(e, 'slice', field)}
          />
        )}
      </ActionTooltip>
    );
  };

  /**
   * Render an individual field.
   *
   * FactorName is ONLY used for display.
   * FactorInternalName is used for React keys and
   * remains available to drag/drop callbacks.
   */
  const renderFieldChip = (
    field,
    isLevel2 = false
  ) => {
    const isDropped = Boolean(
      field.isSelected
    );

    const isDisabledByDefault =
      isViewEmpty &&
      field.IsViewEnabled === false;

    const isDisabled =
      isDropped || isDisabledByDefault;

    const classNames = [
      isDropped ? 'disabled' : '',
      isDisabledByDefault
        ? 'disabledByDefault'
        : '',
    ]
      .filter(Boolean)
      .join(' ');

    /**
     * FactorName is always the user-facing label.
     *
     * displayName is retained for search results where
     * we intentionally show:
     *
     * Metric Name (Group Name)
     */
    const displayName =
      isLevel2 && field.displayName
        ? field.displayName
        : field.FactorName;

    return (
      <FieldChipWrapper
        key={field.FactorInternalName}
        className={classNames}
      >
        <FieldChip
          className={classNames}
          selected={isDropped}
          isLevel2={isLevel2}
          aria-pressed={isDropped}
          role="listitem"
          draggable={!isDisabled}
          disabled={isDisabled}
          onDragStart={
            isDisabled
              ? undefined
              : (event) =>
                  onDragStart &&
                  onDragStart(field, event)
          }
        >
          {displayName}
        </FieldChip>

        {!isDisabled &&
          renderActionPills(field)}
      </FieldChipWrapper>
    );
  };

  /**
   * Render a group/accordion header.
   *
   * FactorName -> visible label
   * FactorInternalName -> internal accordion ID
   */
  const renderAccordionHeader = (
    group,
    isExpanded
  ) => {
    return (
      <FieldChipWrapper
        key={group.FactorInternalName}
        className="lhsAccordion"
      >
        <AccordionChip
          className="lhsAccordion"
          isGroupHeader={true}
          isExpanded={isExpanded}
          onClick={() =>
            toggleGroup(
              group.FactorInternalName
            )
          }
        >
          <AccordionLeftGroup>
            <AccordionBullet />

            <AccordionLabel>
              {group.FactorName}
            </AccordionLabel>
          </AccordionLeftGroup>

          {isExpanded ? (
            <StyledRemoveIcon />
          ) : (
            <StyledAddIcon />
          )}
        </AccordionChip>
      </FieldChipWrapper>
    );
  };

  /**
   * Search/filter the tree.
   *
   * Search is performed against FactorName because
   * FactorName is the user-facing label.
   *
   * FactorInternalName is NOT used for display/search.
   */
  const filterFieldTree = (fieldList) => {
    const query = searchTerm
      .trim()
      .toLowerCase();

    if (!query) return fieldList;

    const result = [];

    fieldList.forEach((field) => {
      if (
        field.isGroup &&
        Array.isArray(field.children)
      ) {
        const matchingChildren =
          field.children
            .filter((child) =>
              (child.FactorName || '')
                .toLowerCase()
                .includes(query)
            )
            .map((child) => ({
              ...child,

              /**
               * Keep the original FactorName and
               * FactorInternalName untouched.
               *
               * displayName is only a temporary UI value.
               */
              displayName: `${child.FactorName}`,
            }));

        if (matchingChildren.length > 0) {
          result.push({
            ...field,
            children: matchingChildren,
            isForceExpanded: true,
          });
        }
      } else {
        if (
          (field.FactorName || '')
            .toLowerCase()
            .includes(query)
        ) {
          result.push(field);
        }
      }
    });

    return result;
  };

  const filteredFields =
    filterFieldTree(parsedFields);

  const isSearching =
    Boolean(searchTerm.trim());

  return (
    <PanelContainer
      className="custom-scrollbar"
      component="aside"
      aria-label="Available fields"
    >
      <SearchWrap>
        <SearchTitle>
          Metrics
        </SearchTitle>

        <InfoWrapper>
          <FieldInfo>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.4471 14.2602C12.0241 15.8916 7.73412 16.0124 8.03623 13.8372C8.2175 12.387 8.51962 10.9973 8.76131 9.54716C8.88215 8.64082 8.27792 8.94293 7.79454 8.76167C7.432 8.5804 7.432 8.15744 7.85496 8.09701C8.39877 7.97617 10.2719 8.03659 10.9365 8.03659C11.2991 8.03659 11.4803 8.21786 11.4803 8.51997C11.5408 8.82209 11.4803 9.1242 11.4199 9.3659C11.1782 10.7556 10.8157 12.2058 10.6948 13.5351C10.6344 13.8372 10.6948 14.381 11.1178 14.381C11.5408 14.3206 11.722 14.1393 12.0846 14.0185C12.3263 13.8976 12.4471 14.0789 12.4471 14.2602Z"
                fill={COLORS.svgInfoFill}
              />

              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.3323 4.47168C11.1782 4.47168 11.7825 5.13633 11.7825 5.92183C11.7825 6.76775 11.1178 7.4324 10.3323 7.37198C9.54682 7.37198 8.88217 6.70733 8.88217 5.92183C8.88217 5.13633 9.54682 4.47168 10.3323 4.47168Z"
                fill={COLORS.svgInfoFill}
              />

              <path
                d="M9.96979 0C15.5287 0 20 4.4713 20 9.96979C20 15.5287 15.5287 20 9.96979 20C4.4713 20 0 15.5287 0 9.96979C0 4.4713 4.4713 0 9.96979 0ZM9.96979 1.51057C5.31722 1.51057 1.51057 5.31722 1.51057 9.96979C1.51057 14.6828 5.31722 18.429 9.96979 18.429C14.6828 18.429 18.429 14.6828 18.429 9.96979C18.429 5.31722 14.6828 1.51057 9.96979 1.51057Z"
                fill={COLORS.svgInfoFill}
              />
            </svg>
          </FieldInfo>

          {renderActionPills(null)}
        </InfoWrapper>

        <SearchInputBox>
          <StyledInputBase
            placeholder="Search Metrics"
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(e.target.value)
            }
            inputProps={{
              'aria-label': 'Search Metrics',
            }}
          />
        </SearchInputBox>
      </SearchWrap>

      <FieldsList role="list">
        {filteredFields.map((field) => {
          if (field.isGroup) {
            /**
             * Use FactorInternalName for expansion state.
             *
             * This avoids problems when two groups have the
             * same display name.
             */
            const isExpanded =
              isSearching ||
              Boolean(
                expandedGroups[
                  field.FactorInternalName
                ] ||
                  field.isForceExpanded
              );

            return (
              <React.Fragment
                key={field.FactorInternalName}
              >
                {renderAccordionHeader(
                  field,
                  isExpanded
                )}

                {isExpanded &&
                  field.children &&
                  field.children.length > 0 && (
                    <SubItemsContainer className="lhsAccordion">
                      {field.children.map(
                        (child) =>
                          renderFieldChip(
                            child,
                            true
                          )
                      )}
                    </SubItemsContainer>
                  )}
              </React.Fragment>
            );
          }

          return renderFieldChip(
            field,
            false
          );
        })}
      </FieldsList>
    </PanelContainer>
  );
}